import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { fetchAdoptedPetDetails } from "./service";
import { isNullOrEmpty } from "./util";
import Pet404 from "./Pet404.tsx";
import shareIconFB from "./assets/images/share_fb.svg";
import shareIconInsta from "./assets/images/share_insta.svg";
import shareIconEmail from "./assets/images/share_email.svg";
import noDogPhoto from "./assets/images/dog_placeholder.jpg";
import noCatPhoto from "./assets/images/cat_placeholder.jpg";
import noBunnyPhoto from "./assets/images/bunny_placeholder.jpg";

export default function AdoptedPetDetailPage() {
  const [petId, setPetId] = useState<string>();
  const [tempPetId, setTempPetId] = useState<string>();

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get("petId");
    console.log("Initializing with pet ID:", id);
    if (isNullOrEmpty(id)) return;
    setPetId(id);
    setTempPetId(id);
  }, []);

  if (isNullOrEmpty(petId)) {
    return null;
  }

  // attempt to set the canonical tag
  useEffect(() => {
    const canonicalUrl = `https://www.seattlehumane.org/pet-detail/?pet=${petId}`;
    // Check if a canonical tag already exists
    let link = document.querySelector("link[rel='canonical']");
    if (!link) {
        // Create a new canonical link tag
        console.log("setting canonical tag (!link) with ", link);
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
    }
    // Update the canonical URL
    console.log("setting canonical tag with ", link);
    link.setAttribute("href", canonicalUrl);
}, [petId]);

  return (
    <>
      <div className="flex items-center gap-2 border-1 border-black outline p-3 mb-8 rounded-md">
        <input
          value={tempPetId}
          onChange={(e) => setTempPetId(e.target.value)}
        />
        <button onClick={() => setPetId(tempPetId)}>Go</button>
      </div>
      <AdoptedPetDetailWrapper adoptionId={petId} />
    </>
  );
}

type PetDetailWrapperProps = {
  adoptionId: string | number;
};

export function AdoptedPetDetailWrapper({ adoptionId }: PetDetailWrapperProps) {
  const [petDetails, setPetDetails] = useState<any>(undefined);

  useEffect(() => {
    (async () => {
      setPetDetails(undefined);
      const result = await fetchAdoptedPetDetails(adoptionId);
      setPetDetails(result);
    })();
  }, [adoptionId]);

  if (petDetails === undefined) {
    // show some loading state
    return <Skeleton count={5} />;
  }

  return <PetDetail petDetails={petDetails} />;
}

type PetDetailProps = {
  petDetails: any;
};

export function PetDetail({ petDetails }: PetDetailProps) {
  // added by DML David to handle when the animalID doesn't actually return anything valid
  if (!("happyTailDetailItem" in petDetails.arrayOfHappyTailDetailItem)) {
    return <Pet404 />;
  }

  const item = petDetails.arrayOfHappyTailDetailItem.happyTailDetailItem;

  // Determine a safe photo for pets without valid photos
  const safePetPhoto = /[A-Za-z0-9]/.test(item.photo)
    ? item.photo
    : {
        dog: noDogPhoto,
        cat: noCatPhoto,
        bunny: noBunnyPhoto,
      }[item.species?.toLowerCase()] || noDogPhoto; // Fallback to noDogPhoto

  // make gender nicer -- english words instead of single letters
  let petGender = "";
  switch (item.sex) {
    case "M":
      petGender = "Male";
      break;
    case "F":
      petGender = "Female";
      break;
    default:
      petGender = "Unknown";
  }

  // make Adoption Date prettier
  const date = new Date(item.adoptionDate);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  const formattedDate = `${month}/${day}/${year}`;

  // stuff for the email mailto share link
  const emailSubject = `Take a look at ${item.name}'s profile`;
  const emailAddress = "";
  const emailBody = `
Hi there!

I thought you’d enjoy seeing this adorable pet on the Seattle Humane website. Here’s a quick peek at ${item.name}:

https://www.seattlehumane.org/pet-detail/?pet=${item.animalId}

Let me know what you think!

Cheers,
[Your Name]
`;
  const mailtoLink = `mailto:${emailAddress}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

  const fbShareParams = new URLSearchParams();
  fbShareParams.append(
    "u",
    `https://www.seattlehumane.org/pet-detail/?pet=${item.id}`,
  );
  const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?${fbShareParams.toString()}`;

  return (
    <>
      <div className="flex flex-col items-center gap-4">
        <img
          className="w-auto h-72 rounded-md"
          src={safePetPhoto}
          alt={`photo of ${item.name}`}
        />

        <div
          className="text-center text-[3rem] sm:text-[3.9rem] text-primary-dark mr-2 mb-4"
          style={{ fontFamily: "Varela Round", lineHeight: "3.5rem" }}
        >
          Meet {item.name}
        </div>

        <section className="flex flex-wrap gap-4 justify-center items-start mb-8">
          <div className="grid grid-cols-full_full px-4 sm:grid-cols-[180px_220px] lg:grid-cols-[140px_160px] mb-4">
            {item.species.toLowerCase() !== "cat" &&
              item.species.toLowerCase() !== "dog" && (
                <>
                  <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
                    Species
                  </div>
                  <div className="border-b-[1px] mb-2 text-wrap">
                    {item.species}
                  </div>
                </>
              )}
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Species
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">{item.species}</div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Looks Like
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">
              {item.primaryBreed}&nbsp;{item.secondaryBreed}
            </div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Age
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">{item.ageGroup}</div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Gender
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">{petGender}</div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Color
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">
              {item.primaryColor}&nbsp;{item.secondaryColor}
            </div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Adoption Date
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">{formattedDate}</div>
            <div className="font-bold mb-2 border-b-0 sm:border-b-[1px]">
              Animal ID
            </div>
            <div className="border-b-[1px] mb-2 text-wrap">
              {item.adoptionId}
            </div>
          </div>

          <div className="description w-full px-4 sm:px-2 sm:w-[600px] md:w-[600px] md:pl-8 break-words">
            <h3 className="text-2xl font-bold mb-8">About Me</h3>
            {item.dsc && item.dsc.trim() !== "" ? (
              <p
                dangerouslySetInnerHTML={{
                  __html: item.dsc,
                }}
              />
            ) : (
              <p>Stay tuned for more details about me!</p>
            )}
            <div className="mb-4" />

            <div className="mb-8">
              <h4 className="text-xl font-bold text-primary-dark mb-2">
                Share my profile
              </h4>

              <div className="share-icons">
                <div className="mb-4">
                  <a
                    className="hover:text-red-600 no-underline"
                    href={fbShareUrl}
                  >
                    <img
                      src={shareIconFB}
                      style={{
                        width: "25px",
                        height: "25px",
                        marginRight: "8px",
                        filter:
                          "invert(32%) sepia(22%) saturate(6785%) hue-rotate(342deg) brightness(111%) contrast(88%)",
                        display: "inline-block",
                      }}
                    />
                    <span>Post my profile on Facebook</span>
                  </a>
                </div>
                <div className="mb-4">
                  <a
                    className="hover:text-red-600 no-underline"
                    href={"https://www.instagram.com/seattlehumane/"}
                  >
                    <img
                      src={shareIconInsta}
                      style={{
                        width: "25px",
                        height: "25px",
                        marginRight: "8px",
                        filter:
                          "invert(32%) sepia(22%) saturate(6785%) hue-rotate(342deg) brightness(111%) contrast(88%)",
                        display: "inline-block",
                      }}
                    />
                    <span>Join us on Instagram</span>
                  </a>
                </div>
                <div className="mb-4">
                  <a
                    className="hover:text-red-600 no-underline"
                    href={mailtoLink}
                  >
                    <img
                      src={shareIconEmail}
                      style={{
                        width: "25px",
                        height: "25px",
                        marginRight: "8px",
                        filter:
                          "invert(32%) sepia(22%) saturate(6785%) hue-rotate(342deg) brightness(111%) contrast(88%)",
                        display: "inline-block",
                      }}
                    />
                    <span>Email a friend {item.name}'s profile</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* <pre className="bg-slate-100 overflow-x-scroll">
        <code>{JSON.stringify(petDetails, undefined, 2)}</code>
      </pre> */}
    </>
  );
}
